import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';
import { Moment } from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, first, Subject } from 'rxjs';
import { LegalizationService } from '../../../data/legalization.service';
import { LegalizationDocumentFileTypeDto } from '../../../models/dtos/legalization-document-files-type-dto';
import { SaveLegalizationInfoBaseComponent } from '../base/save-legalization-info-base.component';
import { AddLegalizationInformationModalData } from '../AddLegalizationInformationModalData';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { CorrectLegalizationDocumentBasicDto, LegalizationDocumentDto } from '../../../models/dtos/legalization-document-files-dto';
import { LegalizationDocumentInfoDto } from '../../../models/dtos/legalization-document-info-dto';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
  selector: 'app-save-legalization-info-by-coordinator',
  templateUrl: './save-legalization-info-by-coordinator.component.html',
  styleUrl: './save-legalization-info-by-coordinator.component.scss',
  standalone: false
})

export class SaveLegalizationInfoByCoordinatorComponent extends SaveLegalizationInfoBaseComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<AddLegalizationInformationModalData>>(MatDialogRef);
  data = inject<AddLegalizationInformationModalData>(MAT_DIALOG_DATA);
  private formBuilder = inject(UntypedFormBuilder);
  private spinner = inject(NgxSpinnerService);
  private legalizationService = inject(LegalizationService);
  private snackbar = inject(SnackBarService);

  public isDocumentUploaded: boolean = false;
  public formGroup: UntypedFormGroup;
  public hasExpirationStartDate: boolean = false;
  public hasExpirationEndDate: boolean = false;

  private legalizationFileTypeName: string;

  private readonly unsubscribe$ = new Subject<void>();

  selectedDocument: LegalizationDocumentInfoDto | null;

  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  constructor() {
    super();
  }

  get legalizationDocumentTypeId() {
    return this.formGroup.get('legalizationDocumentTypeId') as UntypedFormControl;
  }
  get startDate() {
    return this.formGroup.get('startDate') as UntypedFormControl;
  }
  get endDate() {
    return this.formGroup.get('endDate') as UntypedFormControl;
  }
  get description() {
    return this.formGroup.get('description') as UntypedFormControl;
  }
  get documentCategory() {
    return this.formGroup.get('documentCategory') as UntypedFormControl;
  }
  get documentStatus() {
    return this.formGroup.get('documentStatus') as UntypedFormControl;
  }
  get comment() {
    return this.formGroup.get('comment') as UntypedFormControl;
  }
  get status() {
    return this.formGroup.get('status') as UntypedFormControl;
  }
  get legalizationsIds() {
    return this.formGroup.get('agreements') as UntypedFormControl;
  }
  get documentNumber() {
    return this.formGroup.get('documentNumber') as UntypedFormControl;
  }
  get submissionDate() {
    return this.formGroup.get('submissionDate') as UntypedFormControl;
  }
  get agreements() {
    return this.formGroup.get('agreements') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.createForm();
    if (this.data.WorkerId) {
      this.agreements.setValidators([Validators.required]);
    }

    this.agreements.updateValueAndValidity();

    if (this.data.LegalizationDocumentId) {
      this.loadLegalizationDocument(this.data.LegalizationDocumentId);
    }

    if (this.data.IsCorrection) {
      this.disableFormFieldsForCorrection();
    }
  }

  private createForm() {
    this.formGroup = this.formBuilder.group({
      legalizationDocumentTypeId: [null, [Validators.required]],
      description: [null],
      startDate: [null],
      endDate: [null],
      comment: [null, [Validators.maxLength(300)]],
      documentCategory: [null, [Validators.required]],
      agreements: [[]],
      documentNumber: [null, [Validators.maxLength(40)]]
    });
  }

  isSendWorkerFileButtonEnabled(): boolean {
    return this.isDocumentUploaded &&
      this.isFileCountValid() &&
      !!this.legalizationDocumentTypeId.value &&
      this.formGroup.valid;
  }

  public documentStartDateFilter = (date: Moment): boolean => !this.endDate.value || date.isSameOrBefore(this.endDate.value);

  public documentEndDateFilter = (date: Moment): boolean => (!this.startDate.value || date.isSameOrAfter(this.startDate.value)) && date?.isSameOrAfter(moment().startOf('day'));

  createLegalizationDocument(): void {
    if (!this.files || this.formGroup.invalid) {
      return;
    }

    let legalizationDocument: LegalizationDocumentDto = {
      WorkerId: this.data.WorkerId,
      LegalizationDocumentTypeId: this.legalizationDocumentTypeId.value,
      StartDate: this.startDate.value,
      EndDate: this.endDate.value,
      Files: this.result,
      Name: this.legalizationFileTypeName,
      Comment: this.comment.value,
      LegalizationDocumentCategoryId: Number(this.documentCategory.value),
      DocumentNumber: this.documentNumber.value,
      Description: this.description.value,
      CreatedOn: new Date(),
      LegalizationsIds: this.legalizationsIds.value
    };

    this.data.WorkerId ? this.onSave(legalizationDocument) : this.onAddDocument(legalizationDocument);
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  onSave(legalizationInformation: LegalizationDocumentDto): void {
    this.spinner.show();

    const handleSuccess = () => {
      if (this.data.IsCorrection) {
        this.snackbar.openSuccessSnackBar('Legalization.SuccessfullyCorrected');
      }
      else {
        this.snackbar.openSuccessSnackBar('Legalization.SuccessfullySaved');
      }
      this.files = [];
      this.dialogRef.close({ isSuccess: true });
    };

    const observable = this.data.IsCorrection
      ? this.legalizationService.correctLegalizationDocumentByEmployee({
        Id: this.data.LegalizationDocumentId!,
        Files: legalizationInformation.Files
      } as CorrectLegalizationDocumentBasicDto)
      : this.legalizationService.saveLegalizationDocument(legalizationInformation);

    observable.pipe(
      first(),
      finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => handleSuccess(),
      });
  }

  onAddDocument(legalizationDocument: LegalizationDocumentDto): void {
    this.files = [];
    this.dialogRef.close({ isSuccess: true, legalizationDocument: legalizationDocument });
  }

  onLegalizationDocumentTypeSelected(type: LegalizationDocumentFileTypeDto): void {
    this.legalizationDocumentTypeId.setValue(type.Id);
    this.hasExpirationStartDate = type.HasExpirationStartDate || false;
    this.hasExpirationEndDate = type.HasExpirationEndDate || false;

    this.updateValidatorBasedOnCondition(this.startDate, this.hasExpirationStartDate);
    this.updateValidatorBasedOnCondition(this.endDate, this.hasExpirationEndDate);

    this.legalizationFileTypeName = type.Name;
    this.description.setValue(type.Description);
    this.documentCategory.setValue(
      type.LegalizationDocumentCategoryId?.toString() || ''
    );
  }

  onAgreementsSelectionChange(selectedIds: number[]): void {
    this.legalizationsIds.setValue(selectedIds);
  }

  onLegalizationTypeSelected(type: LegalizationDocumentFileTypeDto): void {
    this.legalizationDocumentTypeId.setValue(type.Id);
    this.hasExpirationStartDate = type.HasExpirationStartDate || false;
    this.hasExpirationEndDate = type.HasExpirationEndDate || false;

    this.updateValidatorBasedOnCondition(this.startDate, this.hasExpirationStartDate);
    this.updateValidatorBasedOnCondition(this.endDate, this.hasExpirationEndDate);

    this.legalizationFileTypeName = type.Name;
    this.description.setValue(type.Description);
    this.documentCategory.setValue(
      type.LegalizationDocumentCategoryId?.toString() || ''
    );
  }

  private loadLegalizationDocument(documentId: number): void {
    this.spinner.show();
    this.legalizationService.getLegalizationDocumentByCoordinator(documentId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide())
      )
      .subscribe(document => {
        if (document) {
          this.selectedDocument = document;
          this.legalizationDocumentTypeId.setValue(document.LegalizationDocumentTypeId);
          this.startDate.setValue(document.StartDate);
          this.endDate.setValue(document.EndDate);
          this.documentNumber.setValue(document.DocumentNumber);
          this.comment.setValue(document.Comment);
          this.documentCategory.setValue(document.LegalizationDocumentCategoryId.toString());
          this.description.setValue(document.Description);
        }
      });
  }

  get preSelectedAgreementsArray(): number[] {
    if (this.selectedDocument && this.selectedDocument.LegalizationId) {
      return [this.selectedDocument.LegalizationId];
    }
    return [];
  }

  private disableFormFieldsForCorrection(): void {
    this.legalizationDocumentTypeId.disable();
    this.documentCategory.disable();
    this.comment.disable();
    this.startDate.disable();
    this.endDate.disable();
    this.documentNumber.disable();
    this.description.disable();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
}
